import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import ArchivePurposeDropdown from 'archive/components/ArchivePurposeDropdown';
import {pluralize} from 'BenchlingUtil';
import DropdownFilterForm from 'search/filters/base/DropdownFilterForm';
import {FilterStyle} from 'search/filters/base/FilterFormConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize} from 'ui-library/buttons/Button';
import PlainSearchInput from 'ui-library/PlainSearchInput';
import {HookSetState, InputChangeEvent} from 'util/Types';

import {RelevantTemplatesTableQuery} from './__generated/RelevantTemplatesTableQuery';
import {COLLECTIONS_FILTER_STRING} from './RelevantTemplatesConstants';
import {RelevantTemplatesListingCriteria} from './useRelevantTemplatesListing';

type TemplateCollection =
  RelevantTemplatesTableQuery['relevantTemplatesListing']['accessibleCollections'][number];

export type RelevantTemplatesListingCriteriaUpdater = (
  state: RelevantTemplatesListingCriteria
) => RelevantTemplatesListingCriteria;

export interface RelevantTemplatesCriteriaFormProps {
  listingCriteria: RelevantTemplatesListingCriteria;
  selectedArchivePurposeFilters: Array<string>;
  templateCollections: Array<TemplateCollection>;
  onChange: HookSetState<RelevantTemplatesListingCriteria>;
  onChangeSelectedArchivePurposeFilters: HookSetState<Array<string>>;
}

export default function RelevantTemplatesCriteriaForm({
  listingCriteria,
  selectedArchivePurposeFilters,
  templateCollections,
  onChange,
  onChangeSelectedArchivePurposeFilters,
}: RelevantTemplatesCriteriaFormProps): JSX.Element {
  function handleSearchTermChange(e: InputChangeEvent): void {
    const searchTerm = e.target.value;
    onChange((listingCriteria: RelevantTemplatesListingCriteria) => ({
      ...listingCriteria,
      searchTerm,
    }));
  }

  function handleCollectionFilterChange(collectionIds: Array<string>): void {
    onChange((listingCriteria: RelevantTemplatesListingCriteria) => ({
      ...listingCriteria,
      collectionIds,
    }));
  }

  function makeCollectionDropdownText(numberSelected: number): string {
    if (numberSelected) {
      return `${numberSelected} ${pluralize(numberSelected, COLLECTIONS_FILTER_STRING)}`;
    } else {
      return COLLECTIONS_FILTER_STRING;
    }
  }

  // Sorted by lowercase on backend
  const collectionOptions = templateCollections.map(({id, name}) => ({
    name,
    value: id,
  }));

  return (
    <div
      className={classNames(css(styles.flexDisplay))}
      {...getTestProps({component: 'RelevantTemplatesCriteriaForm', element: 'root'})}
    >
      <PlainSearchInput
        className={classNames(css(styles.queryInput))}
        placeholder="Search"
        onChange={handleSearchTermChange}
        value={listingCriteria.searchTerm}
      />
      <div className={classNames(css(styles.flexDisplay, styles.dropdownFilterForm))}>
        <DropdownFilterForm
          buttonSize={ButtonSize.SMALL}
          dropdownToggleText={makeCollectionDropdownText(listingCriteria.collectionIds.length)}
          filterStyle={FilterStyle.PRIMARY}
          isActive={listingCriteria.collectionIds.length > 0}
          isDisabled={templateCollections.length === 0}
          isMulti={true}
          isSearchable={false}
          onChangeValue={handleCollectionFilterChange}
          optionDatas={collectionOptions}
          value={listingCriteria.collectionIds}
        />
      </div>
      <div className={classNames(css(styles.flexDisplay))}>
        <ArchivePurposeDropdown
          itemIds={templateCollections.map(({id}) => id)}
          onChangePurposes={onChangeSelectedArchivePurposeFilters}
          selectedPurposes={selectedArchivePurposeFilters}
        />
      </div>
    </div>
  );
}
const styles = StyleSheet.create({
  queryInput: {
    width: 350,
    marginRight: 6,
  },
  dropdownFilterForm: {
    marginRight: 6,
  },
  flexDisplay: {
    display: 'flex',
  },
});
